import { Order, OrderEditModel, OrderEventItems } from '@b2b/types/order';
import { CouponVo, WillCallOrderItemsByEventVo } from '@b2b/types/order/api/will-call-order-detail.type';
import { DiscountPriceListEntry, EventInstancesCouponAllPricelist } from '@b2b/common/services/api/eventApi/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { MembershipCardholder } from '@b2b/types/membership';

export function partitionAddonsAndFeesLast<T extends { eventId: string }>(eventItems: T[]): T[] {
  const real: T[] = [];
  const pseudo: T[] = [];
  for (const item of eventItems) {
    const id = item.eventId.toLowerCase();
    if (id === 'addons' || id === 'fees') {
      pseudo.push(item);
    } else {
      real.push(item);
    }
  }
  return [...real, ...pseudo];
}

/**
 * Get membershipId from an order.
 * Checks both the order-level membershipId and membership items in eventItems.
 *
 * @param order - The order object (can be Order, OrderEditModel, undefined, or null)
 * @returns The membershipId if found, null otherwise
 *
 * @example
 * ```typescript
 * // Order with direct membershipId
 * getMembershipIdFromOrder({ membershipId: '12345', eventItems: [] }); // returns '12345'
 *
 * // Order with membershipId in event items
 * getMembershipIdFromOrder({
 *   membershipId: null,
 *   eventItems: [{ items: [{ membershipId: '67890' }] }]
 * }); // returns '67890'
 *
 * // Order with no membershipId
 * getMembershipIdFromOrder({ membershipId: null, eventItems: [] }); // returns null
 *
 * // Undefined/null order
 * getMembershipIdFromOrder(undefined); // returns null
 * ```
 */
/**
 * Get accountCategoryId from an order.
 * Searches membership items in eventItems for an accountCategoryId.
 *
 * @param order - The order object (can be Order, OrderEditModel, undefined, or null)
 * @returns The accountCategoryId if found, null otherwise
 *
 * @example
 * ```typescript
 * getAccountCategoryIdFromOrder({
 *   membershipId: null,
 *   eventItems: [{ items: [{ accountCategoryId: 'cat-123' }] }]
 * }); // returns 'cat-123'
 *
 * getAccountCategoryIdFromOrder({ membershipId: null, eventItems: [] }); // returns null
 * ```
 */
export function getAccountCategoryIdFromOrder(order: Order | OrderEditModel | undefined | null): string | null {
  if (!order) {
    return null;
  }

  for (const eventItem of ensureArray(order.eventItems as any)) {
    for (const item of ensureArray(eventItem.items as any)) {
      if (item.accountCategoryId) {
        return String(item.accountCategoryId);
      }
    }
  }

  return null;
}

export function getMembershipIdFromOrder(order: Order | OrderEditModel | undefined | null): string | null {
  // Guard against undefined/null order
  if (!order) {
    return null;
  }

  // First check if order has a direct membershipId
  if (order.membershipId && order.membershipId !== 'null' && order.membershipId !== 'undefined') {
    return order.membershipId;
  }

  // Otherwise, look for membership items in eventItems
  for (const eventItem of ensureArray(order.eventItems as any)) {
    for (const item of ensureArray(eventItem.items as any)) {
      if (item.membershipId) {
        // Convert to string if it's a number (WillCallItemVo uses number, OrderEventItem uses string)
        return String(item.membershipId);
      }
    }
  }

  return null;
}

/**
 * Extract coupons that have already been applied to order items.
 * Searches through all event items and collects CouponVo objects from item.couponDetail.
 *
 * @param eventItems - Array of event items from the order
 * @returns Array of applied coupons
 *
 * @example
 * ```typescript
 * const eventItems = [
 *   { items: [{ couponDetail: { code: 'SAVE10', name: 'Save 10%' } }] }
 * ];
 * extractAppliedCoupons(eventItems); // returns [{ code: 'SAVE10', name: 'Save 10%' }]
 * ```
 */
export function extractAppliedCoupons(eventItems: WillCallOrderItemsByEventVo[]): CouponVo[] {
  const appliedCoupons: CouponVo[] = [];

  for (const eventItem of ensureArray(eventItems)) {
    for (const item of ensureArray(eventItem.items)) {
      if (item.couponDetail) {
        appliedCoupons.push(item.couponDetail);
      }
    }
  }

  return appliedCoupons;
}

/**
 * Extended discount information including price list metadata and coupon details.
 */
export interface DiscountInfo extends DiscountPriceListEntry {
  discountId: string;
  discountName: string;
  couponDetail?: CouponVo;
}

/**
 * Find if there's a discount for a specific ticket type in an event.
 * Checks if any applied coupons have a discount for the given eventId and ticketTypeId.
 *
 * @param eventId - The event ID to check
 * @param ticketTypeId - The ticket type ID (personTypeId) to check
 * @param appliedCoupons - Array of coupons that have been applied to the order
 * @param eventCoupons - Array of available coupon price lists for events
 * @returns The DiscountInfo with price list metadata if found, null otherwise
 *
 * @example
 * ```typescript
 * const appliedCoupons = [{ code: 'SAVE10' }];
 * const eventCoupons = [{
 *   couponCode: 'SAVE10',
 *   eventPriceLists: [{
 *     eventId: '123',
 *     priceList: {
 *       discountId: 'disc-1',
 *       discountName: 'Summer Sale',
 *       discountPriceListEntries: [
 *         { personTypeId: '456', discountedPrice: '50.00', discountType: 'PercentageBased', discountValue: '10' }
 *       ]
 *     }
 *   }]
 * }];
 * findTicketDiscounted('123', '456', appliedCoupons, eventCoupons);
 * // returns { personTypeId: '456', discountedPrice: '50.00', discountId: 'disc-1', discountName: 'Summer Sale', discountType: 'PercentageBased', discountValue: '10' }
 * ```
 */
export function findTicketDiscounted(
  eventId: string,
  ticketTypeId: string,
  appliedCoupons: CouponVo[],
  eventCoupons: EventInstancesCouponAllPricelist[],
): DiscountInfo | null {
  // Get the coupon codes from applied coupons
  const appliedCouponCodes = appliedCoupons.map((coupon) => coupon.code).filter(Boolean);

  if (appliedCouponCodes.length === 0) {
    return null;
  }

  // Search through event coupons for matching applied coupons
  for (const eventCoupon of eventCoupons) {
    // Check if this coupon is in the applied coupons list
    if (!appliedCouponCodes.includes(eventCoupon.couponCode)) {
      continue;
    }

    // Find the price list for the specific event
    const eventPriceList = eventCoupon.eventPriceLists.find((epl) => epl.eventId === eventId);

    if (!eventPriceList) {
      continue;
    }

    // Find the discount entry for the specific ticket type (personTypeId)
    const discountEntry = eventPriceList.priceList.discountPriceListEntries.find(
      (entry) => entry.personTypeId === ticketTypeId,
    );

    if (discountEntry) {
      // Find the matching coupon object from appliedCoupons
      const matchingCoupon = appliedCoupons.find((coupon) => coupon.code === eventCoupon.couponCode);

      // Return discount entry with price list metadata and coupon details
      return {
        ...discountEntry,
        discountId: eventPriceList.priceList.discountId,
        discountName: eventPriceList.priceList.discountName,
        couponDetail: matchingCoupon,
      };
    }
  }

  return null;
}

/**
 * Minimal discount result returned when a ticket has a discounted price.
 * Covers both coupon-based discounts (DiscountInfo) and membership/reseller discounts.
 */
export interface TicketDiscount {
  discountedPrice: string;
  discountedBasePrice?: string;
  discountId?: string;
  discountName?: string;
  discountType?: string;
  discountValue?: string;
  couponDetail?: CouponVo;
}

export interface ResolveTicketDiscountOptions {
  eventId: string;
  ticketTypeId: string;
  appliedCoupons: CouponVo[];
  eventCoupons: EventInstancesCouponAllPricelist[];
  priceListPrices?: Array<{
    personType?: { id?: string };
    price?: string | number;
    discountedPrice?: string | number;
    basePrice?: string | number;
    discountedBasePrice?: string | number;
    discountId?: string;
    discountName?: string;
  }>;
}

/**
 * Resolves a discount for a specific ticket type by checking both coupon-based
 * discounts and membership/reseller discounts from the event price list.
 *
 * Coupon discounts take priority. If none is found, falls back to checking price
 * list entries where discountedPrice < price (set by the API or applyResellerDiscounts).
 *
 * @returns A TicketDiscount if any discount is found, null otherwise
 */
export function resolveTicketDiscount(options: ResolveTicketDiscountOptions): TicketDiscount | null {
  const { eventId, ticketTypeId, appliedCoupons, eventCoupons, priceListPrices } = options;

  // First try coupon-based discount lookup
  const couponDiscount = findTicketDiscounted(eventId, ticketTypeId, appliedCoupons, eventCoupons);
  if (couponDiscount) {
    return couponDiscount;
  }

  // Fall back to membership/reseller discounts in the event price list
  const prices = priceListPrices ?? [];
  const priceEntry = prices.find(
    (p) =>
      p.personType?.id === ticketTypeId &&
      p.discountedPrice !== undefined &&
      p.price !== undefined &&
      Number(p.discountedPrice) < Number(p.price),
  );

  if (!priceEntry) {
    return null;
  }

  return {
    discountedPrice: String(priceEntry.discountedPrice),
    // Tax-excluded discounted price, when the price list provides it (sales-tax tenants)
    ...(priceEntry.discountedBasePrice !== undefined && {
      discountedBasePrice: String(priceEntry.discountedBasePrice),
    }),
    discountId: priceEntry.discountId ?? '',
    discountName: priceEntry.discountName ?? '',
  };
}

/**
 * Apply coupon discounts to event items.
 * Returns a new array of event items with updated discount information including:
 * discountedUnitPrice, discountId, discountName, discountType, and discountValue.
 * Does not mutate the original event items.
 *
 * @param eventItems - Array of event items from the order
 * @param options.appliedCoupons - Array of coupons that have been applied to the order
 * @param options.eventCoupons - Array of available coupon price lists for events
 * @param options.originalEventItems - Optional array of event items from the original order.
 *   When provided, items belonging to events in the original order are skipped if
 *   their unitPrice already equals their discountedUnitPrice (no price change).
 * @returns New array of event items with updated discount prices and metadata
 *
 * @example
 * ```typescript
 * const eventItems = [{ eventId: '123', items: [{ ticketTypeId: '456', discountedUnitPrice: '100.00' }] }];
 * const updated = applyDiscountsToEventItems(eventItems, {
 *   appliedCoupons: [{ code: 'SAVE10' }],
 *   eventCoupons: [{ couponCode: 'SAVE10', eventPriceLists: [...] }],
 * });
 * // Returns new array with updated discountedUnitPrice, discountId, discountName, discountType, discountValue
 * ```
 */
export interface ApplyDiscountsOptions {
  appliedCoupons: CouponVo[];
  eventCoupons: EventInstancesCouponAllPricelist[];
  originalEventItems?: Array<{ eventId?: string }>;
}

export function applyDiscountsToEventItems(
  eventItems: WillCallOrderItemsByEventVo[],
  options: ApplyDiscountsOptions,
): WillCallOrderItemsByEventVo[] {
  const { appliedCoupons, eventCoupons, originalEventItems } = options;
  return eventItems.map((event) => {
    const isInOriginalOrder = originalEventItems ? originalEventItems.some((e) => e.eventId === event.eventId) : false;

    return {
      ...event,
      items: event.items.map((item) => {
        const ticketDiscounted =
          event.eventId && item.ticketTypeId
            ? findTicketDiscounted(event.eventId, item.ticketTypeId, appliedCoupons, eventCoupons)
            : null;

        const couponDetail = (ticketDiscounted?.couponDetail as CouponVo) || null;

        if (isInOriginalOrder && item.unitPrice === item.discountedUnitPrice) {
          return item;
        }

        return {
          ...item,
          discountedUnitPrice: ticketDiscounted?.discountedPrice || item.discountedUnitPrice,
          // Keep the tax-excluded discounted price in sync so callers can normalize
          // the display to base pricing when sales tax is enabled.
          discountedBasePrice: ticketDiscounted?.discountedBasePrice ?? item.discountedBasePrice,
          ...(ticketDiscounted && {
            discountId: ticketDiscounted.discountId,
            discountName: ticketDiscounted.discountName,
            discountType: ticketDiscounted.discountType,
            discountValue: ticketDiscounted.discountValue,
          }),
          couponDetail,
        };
      }),
    };
  });
}

/**
 * Add membershipId to all ticket items in event items.
 * Returns a new array of event items with membershipId added to each ticket.
 * Does not mutate the original event items.
 * Only adds membershipId property if membershipId has a truthy value.
 *
 * @param eventItems - Array of event items to add membershipId to
 * @param membershipId - The membershipId to add (string or number)
 * @returns New array of event items with membershipId added to each ticket (only if membershipId is truthy)
 *
 * @example
 * ```typescript
 * const eventItems = [{ eventId: '123', items: [{ ticketTypeId: '456' }] }];
 * const updated = addMembershipIdToEventItems(eventItems, '789');
 * // Returns new array with membershipId: 789 added to each ticket
 *
 * const updatedNull = addMembershipIdToEventItems(eventItems, null);
 * // Returns original array unchanged (no membershipId property added)
 * ```
 */
export function addMembershipIdToEventItems(
  eventItems: WillCallOrderItemsByEventVo[],
  membershipId: string | number | null,
): WillCallOrderItemsByEventVo[] {
  if (!membershipId) {
    return eventItems;
  }

  const numericMembershipId = Number(membershipId);

  return eventItems.map((eventItem) => ({
    ...eventItem,
    items: eventItem.items.map((ticket) => ({
      ...ticket,
      membershipId: numericMembershipId,
    })),
  }));
}

/**
 * Add accountCategoryId to all ticket items in event items.
 * Returns a new array of event items with accountCategoryId added to each ticket.
 * Does not mutate the original event items.
 * Only adds accountCategoryId property if accountCategoryId has a truthy value.
 *
 * @param eventItems - Array of event items to add accountCategoryId to
 * @param accountCategoryId - The accountCategoryId to add
 * @returns New array of event items with accountCategoryId added to each ticket (only if accountCategoryId is truthy)
 */
export function addAccountCategoryIdToEventItems(
  eventItems: WillCallOrderItemsByEventVo[],
  accountCategoryId: string | null,
): WillCallOrderItemsByEventVo[] {
  if (!accountCategoryId) {
    return eventItems;
  }

  return eventItems.map((eventItem) => ({
    ...eventItem,
    items: eventItem.items.map((ticket) => ({
      ...ticket,
      accountCategoryId,
    })),
  }));
}

/**
 * Calculate the original subtotal for an item from the original order.
 * Looks up the item in the original order by orderItemId and calculates
 * the subtotal using the original quantity and discounted unit price.
 *
 * @param originalOrder - The original order object containing eventItems
 * @param orderItemId - The order item ID to look up
 * @param eventId - Optional event ID to limit search scope for performance
 * @returns The calculated original subtotal, or 0 if item not found or is newly added
 *
 * @example
 * ```typescript
 * const originalOrder = {
 *   eventItems: [{
 *     eventId: '123',
 *     items: [{ orderItemId: 'item-1', quantity: 2, discountedUnitPrice: '50.00' }]
 *   }]
 * };
 *
 * // Item found in original order
 * calculateOriginalSubtotal(originalOrder, 'item-1'); // returns 100
 *
 * // Item found with eventId optimization
 * calculateOriginalSubtotal(originalOrder, 'item-1', '123'); // returns 100
 *
 * // Newly added item (no orderItemId)
 * calculateOriginalSubtotal(originalOrder, undefined); // returns 0
 *
 * // Item not found in original order
 * calculateOriginalSubtotal(originalOrder, 'item-999'); // returns 0
 * ```
 */
export function calculateOriginalSubtotal(
  originalOrder: { eventItems: WillCallOrderItemsByEventVo[] | OrderEventItems[] },
  orderItemId: string | undefined,
  eventId?: string,
  options?: { useTaxInclusive?: boolean }
): number {
  // Newly added items don't have orderItemId
  if (!orderItemId) {
    return 0;
  }

  // Search for the item in the original order
  for (const event of originalOrder.eventItems) {
    // If eventId is provided, only search in that event for performance
    if (eventId && event.eventId !== eventId) {
      continue;
    }

    const originalItem = event.items.find((i) => i.orderItemId === orderItemId);
    if (originalItem) {
      // Found the item in the original order - use its quantity and discounted price.
      // When requested (order total math), use the preserved tax-inclusive price so the
      // summary reflects real money; otherwise use the (possibly base-normalized) display price.
      const quantity = parseInt(String(originalItem.quantity ?? 0), 10);
      const normalizedQuantity = isNaN(quantity) || quantity < 0 ? 0 : quantity;
      const unitPrice = options?.useTaxInclusive
        ? Number(originalItem.taxInclusiveDiscountedUnitPrice ?? originalItem.discountedUnitPrice ?? 0)
        : Number(originalItem.discountedUnitPrice ?? 0);
      const price = normalizedQuantity * unitPrice;
      return price || 0;
    }
  }

  // Item not found in original order (it's a newly added item)
  return 0;
}

/**
 * Format the primary cardholder's full name with prefix.
 * Filters out empty strings to avoid double spaces.
 *
 * @param cardholder - The cardholder object
 * @returns Formatted string like "Primary: Mr John Doe"
 *
 * @example
 * ```typescript
 * formatPrimaryCardholderName({ prefix: 'Mr', firstName: 'John', lastName: 'Doe' });
 * // returns "Primary: Mr John Doe"
 *
 * formatPrimaryCardholderName({ prefix: '', firstName: 'Jane', lastName: 'Doe' });
 * // returns "Primary: Jane Doe"
 * ```
 */
export function formatPrimaryCardholderName(cardholder: MembershipCardholder): string {
  return ['Primary:', cardholder.prefix ?? '', cardholder.firstName, cardholder.lastName].filter(Boolean).join(' ');
}
